import React from 'react';
import { Plus, Minus, X, Users } from 'lucide-react';
import { formatDateForInput } from './guestReservationFormFormat';
import { useGuestReservationForm } from './useGuestReservationForm';
import { AvailabilityDayPicker } from './AvailabilityDayPicker';

const CANADIAN_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'Colombie-Britannique' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'Nouveau-Brunswick' },
  { value: 'NL', label: 'Terre-Neuve-et-Labrador' },
  { value: 'NS', label: 'Nouvelle-Écosse' },
  { value: 'NT', label: 'Territoires du Nord-Ouest' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Île-du-Prince-Édouard' },
  { value: 'QC', label: 'Québec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

export function GuestReservationFormPersonalAndStayFields({
  m,
  section = 'all',
}: {
  m: ReturnType<typeof useGuestReservationForm>;
  /** Wizard: identity = basic info; stay = dates + groups; all = legacy full block. */
  section?: 'identity' | 'stay' | 'all';
}) {
  const {
    t,
    currentUser,
    itemType,
    itemId,
    paymentMode,
    shoppingCartData,
    formData,
    handleInputChange,
    useDuration,
    onDateChange,
    setFormData,
    isUpdatingFromExternalRef,
    isUpdatingFromSelectRef,
    createAccount,
    setCreateAccount,
    accountPassword,
    setAccountPassword,
  } = m;

  const isGuestCheckout = paymentMode === 'guest' && !currentUser;

  const showIdentity = section === 'identity' || section === 'all';
  const showStay = section === 'stay' || section === 'all';

  return (
    <>
            {/* Personal Information - Only show if not logged in, or show as read-only if logged in */}
            {showIdentity && !currentUser ? (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('guestReservation.personalInfo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="grf-firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('guestReservation.firstName')} *
                    </label>
                    <input
                      id="grf-firstName"
                      type="text"
                      name="firstName"
                      autoComplete="given-name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="grf-lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('guestReservation.lastName')} *
                    </label>
                    <input
                      id="grf-lastName"
                      type="text"
                      name="lastName"
                      autoComplete="family-name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="grf-email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('guestReservation.email')} *
                    </label>
                    <input
                      id="grf-email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="grf-phone" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('guestReservation.phone')} *
                    </label>
                    <input
                      id="grf-phone"
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    />
                  </div>
                </div>
              </div>
            ) : showIdentity && currentUser ? (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('guestReservation.personalInfo')}
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('guestReservation.firstName')}
                      </label>
                      <div className="text-gray-900 font-medium">{formData.firstName}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('guestReservation.lastName')}
                      </label>
                      <div className="text-gray-900 font-medium">{formData.lastName}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('guestReservation.email')}
                      </label>
                      <div className="text-gray-900 font-medium">{formData.email}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('guestReservation.phone')}
                      </label>
                      <div className="text-gray-900 font-medium">{formData.phone || t('common.notProvided')}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Address section — always shown (guest or logged-in) */}
            {showIdentity ? (
            <>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Adresse</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="grf-addressLine1" className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse ligne 1 <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    id="grf-addressLine1"
                    type="text"
                    name="addressLine1"
                    autoComplete="street-address"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    placeholder="123 Rue des Érables"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label htmlFor="grf-postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Code postal *
                  </label>
                  <input
                    id="grf-postalCode"
                    type="text"
                    name="postalCode"
                    autoComplete="postal-code"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    placeholder="H0H 0H0"
                  />
                </div>
                <div>
                  <label htmlFor="grf-province" className="block text-sm font-medium text-gray-700 mb-2">
                    Province *
                  </label>
                  <select
                    id="grf-province"
                    name="province"
                    autoComplete="address-level1"
                    value={formData.province}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500 bg-white"
                  >
                    {CANADIAN_PROVINCES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="grf-country" className="block text-sm font-medium text-gray-700 mb-2">
                    Pays *
                  </label>
                  <select
                    id="grf-country"
                    name="country"
                    autoComplete="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500 bg-white"
                  >
                    <option value="CA">Canada</option>
                    <option value="US">États-Unis</option>
                    <option value="FR">France</option>
                    <option value="BE">Belgique</option>
                    <option value="CH">Suisse</option>
                    <option value="OTHER">Autre</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Create account opt-in — guest checkout only */}
            {isGuestCheckout && (
              <div className="rounded-lg border border-nature-200 bg-nature-50 p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={createAccount}
                    onChange={(e) => setCreateAccount(e.target.checked)}
                    className="w-4 h-4 text-nature-600 border-gray-300 rounded focus:ring-nature-500"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    Créer un compte client en même temps que ma réservation
                  </span>
                </label>
                {createAccount && (
                  <div>
                    <label htmlFor="grf-accountPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Mot de passe pour votre compte *
                    </label>
                    <input
                      id="grf-accountPassword"
                      type="password"
                      autoComplete="new-password"
                      value={accountPassword}
                      onChange={(e) => setAccountPassword(e.target.value)}
                      required={createAccount}
                      minLength={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                      placeholder="Minimum 8 caractères"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Un email de vérification vous sera envoyé après la réservation.
                    </p>
                  </div>
                )}
              </div>
            )}
            </>
            ) : null}

            {/* Dates - Guests removed, managed in booking assistant */}
            {showStay && (itemType === 'accommodation' || itemType === 'package') && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('guestReservation.dates')}
                  </h3>
                  {(formData.checkIn || formData.checkOut) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          checkIn: '',
                          checkOut: '',
                          duration: 1
                        }));
                        if (onDateChange) {
                          queueMicrotask(() => onDateChange(null, null));
                        }
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                      title={t('common.clear')}
                    >
                      <X className="w-3 h-3" />
                      <span>{t('common.clear')}</span>
                    </button>
                  )}
                </div>
                {shoppingCartData?.startDate && shoppingCartData?.endDate && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 italic">
                      {t('guestReservation.fromShoppingAssistant')}
                    </p>
                  </div>
                )}
                {(formData.checkIn || formData.checkOut) && (
                  <div className="mb-3 rounded-xl border border-[#2f9e44]/25 bg-[#2f9e44]/08 px-3 py-2 text-sm text-[#3d2817]">
                    <span className="font-semibold">{t('guestReservation.checkIn')}: </span>
                    {formData.checkIn || '—'}
                    <span className="mx-2 text-[#3d2817]/35">→</span>
                    <span className="font-semibold">{t('guestReservation.checkOut')}: </span>
                    {formData.checkOut || '—'}
                    {formData.duration > 0 ? (
                      <span className="ml-2 text-[#5d4437]">
                        ({formData.duration}{' '}
                        {formData.duration === 1
                          ? t('guestReservation.night')
                          : t('guestReservation.nights')}
                        )
                      </span>
                    ) : null}
                  </div>
                )}

                {useDuration ? (
                  <div className="mb-3">
                    <label className="mb-2 block text-sm font-medium text-[#3d2817]">
                      {t('guestReservation.duration')} *
                    </label>
                    <div className="flex items-center overflow-hidden rounded-xl border border-[#3d2817]/15 bg-white focus-within:ring-2 focus-within:ring-[#2f9e44]/40">
                      <button
                        type="button"
                        onClick={() => {
                          const newDuration = Math.max(1, formData.duration - 1);
                          isUpdatingFromSelectRef.current = true;
                          setFormData((prev) => {
                            if (!prev.checkIn) return { ...prev, duration: newDuration };
                            const [year, month, day] = prev.checkIn.split('-').map(Number);
                            const checkInDate = new Date(year, month - 1, day);
                            const checkOutDate = new Date(checkInDate);
                            checkOutDate.setDate(checkOutDate.getDate() + newDuration);
                            const updated = {
                              ...prev,
                              duration: newDuration,
                              checkOut: formatDateForInput(checkOutDate),
                            };
                            if (onDateChange && !isUpdatingFromExternalRef.current) {
                              queueMicrotask(() =>
                                onDateChange(new Date(checkInDate), new Date(checkOutDate)),
                              );
                            }
                            return updated;
                          });
                        }}
                        disabled={formData.duration <= 1}
                        className="flex items-center justify-center bg-[#f5f1eb] px-3 py-2 transition-colors hover:bg-[#ece3de] disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Decrease"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="flex-1 select-none py-2 text-center font-semibold text-[#3d2817]">
                        {formData.duration}{' '}
                        {formData.duration === 1
                          ? t('guestReservation.night')
                          : t('guestReservation.nights')}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newDuration = formData.duration + 1;
                          isUpdatingFromSelectRef.current = true;
                          setFormData((prev) => {
                            if (!prev.checkIn) return { ...prev, duration: newDuration };
                            const [year, month, day] = prev.checkIn.split('-').map(Number);
                            const checkInDate = new Date(year, month - 1, day);
                            const checkOutDate = new Date(checkInDate);
                            checkOutDate.setDate(checkOutDate.getDate() + newDuration);
                            const updated = {
                              ...prev,
                              duration: newDuration,
                              checkOut: formatDateForInput(checkOutDate),
                            };
                            if (onDateChange && !isUpdatingFromExternalRef.current) {
                              queueMicrotask(() =>
                                onDateChange(new Date(checkInDate), new Date(checkOutDate)),
                              );
                            }
                            return updated;
                          });
                        }}
                        className="flex items-center justify-center bg-[#f5f1eb] px-3 py-2 transition-colors hover:bg-[#ece3de]"
                        aria-label="Increase"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : null}

                {itemId && (
                  <div className="mt-2">
                    <AvailabilityDayPicker
                      accommodationId={itemId}
                      checkIn={formData.checkIn ? new Date(formData.checkIn + 'T12:00:00') : undefined}
                      checkOut={formData.checkOut ? new Date(formData.checkOut + 'T12:00:00') : undefined}
                      onSelectCheckIn={(date) => {
                        if (!date) {
                          setFormData((prev) => ({ ...prev, checkIn: '', checkOut: '' }));
                          if (onDateChange && !isUpdatingFromExternalRef.current) {
                            queueMicrotask(() => onDateChange?.(null as any, null));
                          }
                          return;
                        }
                        const dateStr = formatDateForInput(date);
                        setFormData((prev) => {
                          const updated = { ...prev, checkIn: dateStr };
                          if (useDuration && prev.duration > 0) {
                            const checkOut = new Date(date);
                            checkOut.setDate(checkOut.getDate() + prev.duration);
                            updated.checkOut = formatDateForInput(checkOut);
                          }
                          return updated;
                        });
                        if (onDateChange && !isUpdatingFromExternalRef.current) {
                          const cIn = new Date(date);
                          queueMicrotask(() => {
                            const out =
                              useDuration && formData.duration > 0
                                ? new Date(date.getTime() + formData.duration * 86400000)
                                : null;
                            onDateChange?.(cIn, out);
                          });
                        }
                      }}
                      onSelectRange={(from, to) => {
                        if (!from || !to) {
                          setFormData((prev) => ({ ...prev, checkIn: '', checkOut: '', duration: 1 }));
                          if (onDateChange && !isUpdatingFromExternalRef.current) {
                            queueMicrotask(() => onDateChange?.(null as any, null));
                          }
                          return;
                        }
                        const nights = Math.max(
                          1,
                          Math.round((to.getTime() - from.getTime()) / 86400000),
                        );
                        setFormData((prev) => ({
                          ...prev,
                          checkIn: formatDateForInput(from),
                          checkOut: formatDateForInput(to),
                          duration: nights,
                        }));
                        if (onDateChange && !isUpdatingFromExternalRef.current) {
                          queueMicrotask(() => onDateChange(from, to));
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Participants — adults + children */}
            {showStay ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('guestReservation.participants')}
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Adults */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.adults')}
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-nature-500">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, adults: Math.max(1, prev.adults - 1) }))}
                      disabled={formData.adults <= 1}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Diminuer adultes"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="flex-1 text-center py-2 font-semibold text-gray-800 select-none">
                      {formData.adults}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, adults: prev.adults + 1 }))}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                      aria-label="Augmenter adultes"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Children */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.children')}
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-nature-500">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, children: Math.max(0, prev.children - 1) }))}
                      disabled={formData.children <= 0}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Diminuer enfants"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="flex-1 text-center py-2 font-semibold text-gray-800 select-none">
                      {formData.children}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, children: prev.children + 1 }))}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                      aria-label="Augmenter enfants"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              {(formData.adults + formData.children) > 0 && (
                <p className="text-xs text-gray-400 mt-2">
                  {formData.adults + formData.children} {t('common.persons')} au total
                  {formData.children > 0 && ` (${formData.adults} ${t('guestReservation.adults').toLowerCase()}, ${formData.children} ${t('guestReservation.children').toLowerCase()})`}
                </p>
              )}
            </div>
            ) : null}

    </>
  );
}

